// @mui
import Box from '@mui/material/Box';
import { PageContainer } from 'src/components/page-container';
// routes
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
// hooks
import { useGetOverlay } from 'src/api/admin';
// components
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import Head from 'src/components/head';
// types
import { IFetchBaseParams, IOverlayItem } from 'src/types';
//
import { LoadingScreen } from 'src/components/loading-screen';
import { RecordUnavailable } from 'src/components/record-unavailable';
import OverlayNewEditForm from '../overlay-new-edit-form';

// ----------------------------------------------------------------------

// Safely parse the startDate and endDate
const withParsedDates = (overlay: IOverlayItem) => ({
  ...overlay,
  startDate: overlay.startDate ? new Date(overlay.startDate) : undefined,
  endDate: overlay.endDate ? new Date(overlay.endDate) : undefined,
});

export default function OverlayEditView({ overlayId }: IFetchBaseParams) {
  const { data: overlay, isLoading } = useGetOverlay({ overlayId });

  const { t } = useLocales('overlayPage');
  const { t: tc } = useLocales('common');

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <LoadingScreen />
      </Box>
    );

  // A failed read leaves no overlay: without this the form below opens in create mode and saving
  // it would create a NEW overlay instead of editing this one. SWR keeps the last good overlay
  // through a failed revalidation, so `error` alone does not hide it.
  const unavailable = !overlay?.id;
  const heading = unavailable ? tc('record_unavailable') : `${t('edit')}`;

  return (
    <>
      <Head title={heading} />
      <PageContainer variant="form">
        <CustomBreadcrumbs
          heading={heading}
          links={[
            { name: t('admin') },
            { name: t('overlays'), href: paths.admin.dashboard.overlays.root },
            ...(unavailable ? [] : [{ name: heading }]),
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        {unavailable ? (
          <RecordUnavailable
            backHref={paths.admin.dashboard.overlays.root}
            backLabel={t('overlays')}
          />
        ) : (
          <OverlayNewEditForm mode="edit" currentOverlay={withParsedDates(overlay)} />
        )}
      </PageContainer>
    </>
  );
}
